import { Injectable, Injector, NgZone, inject } from "@angular/core";
import {
  Auth,
  User,
  PhoneAuthProvider,
  RecaptchaVerifier,
  createUserWithEmailAndPassword,
  deleteUser as firebaseDeleteUser,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  updateEmail as firebaseUpdateEmail,
  fetchSignInMethodsForEmail,
  updatePassword,
  linkWithCredential,
  EmailAuthProvider,
} from "@angular/fire/auth";
import { BehaviorSubject } from "rxjs";
import { map } from "rxjs/operators";
import { Capacitor, type PluginListenerHandle } from "@capacitor/core";

import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { FirebaseAppCheck } from "@capacitor-firebase/app-check";

import { environment } from "../../../environments/environment";

import { StorageService } from "../storage/storage.service";
import { GlobalService } from "../global/global.service";
import { ApiService } from "../api/api.service";
import { Strings } from "../../enum/strings.enum";

import { CartService } from "../cart/cart.service";
import { OrderService } from "../order/order.service";
import { AddressService } from "../address/address.service";
import { PushService } from "../push/push.service";

export class AuthUserId {
  constructor(public uid: string) {}
}

type AnyError = any;

@Injectable({ providedIn: "root" })
export class AuthService {
  public static UNKNOWN_USER = null;

  private storage = inject(StorageService);
  private global = inject(GlobalService);
  private apiService = inject(ApiService);
  private ngZone = inject(NgZone);
  private injector = inject(Injector);
  private fireAuth = inject(Auth);

  private _uid = new BehaviorSubject<AuthUserId | null>(
    AuthService.UNKNOWN_USER
  );

  private recaptchaVerifier: RecaptchaVerifier | null = null;

  // Prefer Gen2 run.app URL
  private readonly NATIVE_AUTH_CUSTOM_TOKEN_URL = (
    environment.nativeAuthCustomTokenUrl || ""
  ).trim();

  // ✅ Native OTP stream (native only)
  private nativeOtpSubject = new BehaviorSubject<string | null>(null);
  nativeOtp$ = this.nativeOtpSubject.asObservable();
  private nativeOtpHandle?: PluginListenerHandle;

  // ✅ SMS cost control: allow only 1 send attempt per phone in a window
  private smsAttemptByPhone = new Map<
    string,
    { count: number; firstAt: number }
  >();
  private readonly SMS_MAX_ATTEMPTS_PER_PHONE = 1;
  private readonly SMS_ATTEMPT_WINDOW_MS = 10 * 60 * 1000; // 10 minutes

  get userId() {
    return this._uid
      .asObservable()
      .pipe(map((uid) => (uid ? uid : AuthService.UNKNOWN_USER)));
  }

  constructor() {
    onAuthStateChanged(this.fireAuth, (user) => {
      this.ngZone.run(() => {
        if (user?.uid) this.setUserData(user.uid);
        else this._uid.next(AuthService.UNKNOWN_USER);
      });
    });

    this.initNativeOtpListener();
  }

  // ---------------- Platform helpers ----------------
  private isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  private isPluginAvailable(name: string): boolean {
    return Capacitor.isPluginAvailable(name);
  }

  // ✅ listens for phoneVerificationCompleted and emits verificationCode if present
  private initNativeOtpListener(): void {
    if (!this.isNative()) return;
    if (!this.isPluginAvailable("FirebaseAuthentication")) return;

    void FirebaseAuthentication.addListener(
      "phoneVerificationCompleted",
      (event: any) => {
        const code = String(event?.verificationCode || "").trim();
        if (code) this.nativeOtpSubject.next(code);
      }
    )
      .then((handle) => (this.nativeOtpHandle = handle))
      .catch(() => {});
  }

  // ---------------- Friendly errors ----------------
  private friendlyAuthError(
    e: AnyError,
    fallback = "حدث خطأ ما. حاول مرة أخرى."
  ): Error {
    const code: string | undefined = e?.code || e?.error?.code;
    const msg: string | undefined = e?.message || e?.error?.message;

    const mapErr: Record<string, string> = {
      "auth/invalid-email": "البريد الإلكتروني غير صالح.",
      "auth/user-disabled": "تم تعطيل هذا الحساب.",
      "auth/user-not-found": "لا يوجد حساب بهذا البريد الإلكتروني.",
      "auth/wrong-password": "كلمة المرور غير صحيحة.",
      "auth/too-many-requests": "طلبات كثيرة. حاول لاحقًا.",
      "auth/email-already-in-use": "هذا البريد الإلكتروني مستخدم بالفعل.",
      "auth/weak-password": "كلمة المرور ضعيفة جدًا.",
      "auth/requires-recent-login": "يجب تسجيل الدخول مجددًا لإتمام العملية.",
      "auth/invalid-verification-code": "رمز التحقق غير صحيح.",
      "auth/missing-verification-code": "أدخل رمز التحقق.",
      "auth/network-request-failed": "مشكلة اتصال بالإنترنت. تحقق من الشبكة.",
      "auth/session-expired": "انتهت صلاحية رمز التحقق. أعد المحاولة.",
      "auth/code-expired": "انتهت صلاحية رمز التحقق. أعد المحاولة.",
      "auth/invalid-verification-id":
        "انتهت صلاحية جلسة التحقق. أعد طلب الرمز.",

      "auth/credential-already-in-use":
        "هذا البريد مستخدم بالفعل. سجّل الدخول به أو اتصل بالدعم لدمج الحسابات.",
      "auth/provider-already-linked": "تمت ترقية الحساب مسبقًا.",
    };

    const finalMsg = (code && mapErr[code]) || msg || fallback;

    const err: any = new Error(finalMsg);
    if (code) err.code = code;
    return err as Error;
  }

  // ---------------- DZ Phone normalize helpers ----------------
  private cleanPhoneInput(v: string): string {
    return String(v || "")
      .trim()
      .replace(/[^\d+]/g, "");
  }

  /**
   * Accepts:
   * - 0550123456
   * - 550123456
   * - +213550123456
   * - 213550123456
   */
  private normalizeDzPhoneAny(raw: string): { e164: string; local10: string } {
    const v = this.cleanPhoneInput(raw);

    // +213XXXXXXXXX
    if (v.startsWith("+")) {
      if (!v.startsWith("+213"))
        throw new Error("أدخل رقمًا جزائريًا صحيحًا مثل: 0551234567");
      const rest = v.substring(4);
      if (!/^[5-7]\d{8}$/.test(rest))
        throw new Error("رقم الهاتف غير صالح. يجب أن يبدأ بـ 05 أو 06 أو 07");
      return { e164: `+213${rest}`, local10: `0${rest}` };
    }

    // 213XXXXXXXXX
    if (v.startsWith("213")) {
      const rest = v.substring(3);
      if (!/^[5-7]\d{8}$/.test(rest))
        throw new Error("رقم الهاتف غير صالح. يجب أن يبدأ بـ 05 أو 06 أو 07");
      return { e164: `+213${rest}`, local10: `0${rest}` };
    }

    // 0XXXXXXXXX (10)
    if (/^0[5-7]\d{8}$/.test(v)) {
      const rest = v.substring(1);
      return { e164: `+213${rest}`, local10: v };
    }

    // XXXXXXXXX (9)
    if (/^[5-7]\d{8}$/.test(v)) {
      return { e164: `+213${v}`, local10: `0${v}` };
    }

    throw new Error("رقم الهاتف غير صالح. يجب أن يبدأ بـ 05 أو 06 أو 07");
  }

  private normalizePhoneE164(phone: string): string {
    // keep for non-DZ usage; in your app DZ is enough
    const trimmed = (phone || "").trim().replace(/\s+/g, "");
    if (!trimmed.startsWith("+") || trimmed.length < 8) {
      throw new Error(
        "رقم الهاتف غير صالح. استخدم الصيغة الدولية مثل: +213XXXXXXXXX"
      );
    }
    return trimmed;
  }

  // ---------------- SMS attempt limiter ----------------
  private enforceSmsAttemptLimit(phoneE164: string): void {
    const now = Date.now();
    const prev = this.smsAttemptByPhone.get(phoneE164);

    if (!prev) {
      this.smsAttemptByPhone.set(phoneE164, { count: 1, firstAt: now });
      return;
    }

    // window expired => reset
    if (now - prev.firstAt > this.SMS_ATTEMPT_WINDOW_MS) {
      this.smsAttemptByPhone.set(phoneE164, { count: 1, firstAt: now });
      return;
    }

    if (prev.count >= this.SMS_MAX_ATTEMPTS_PER_PHONE) {
      throw new Error(
        "تم إرسال SMS مرة واحدة فقط. يمكنك المتابعة عبر البريد الإلكتروني."
      );
    }

    prev.count += 1;
    this.smsAttemptByPhone.set(phoneE164, prev);
  }

  // ---------------- User doc helpers ----------------
  private async getUserDataOrNull(uid: string): Promise<any | null> {
    try {
      const docSnap: any = await this.apiService.getDocById(`users/${uid}`);
      return docSnap?.exists?.() ? docSnap.data() : null;
    } catch {
      return null;
    }
  }

  private async ensureUserDoc(
    uid: string,
    partial: Partial<any>
  ): Promise<any> {
    const existing = await this.getUserDataOrNull(uid);
    if (existing) return existing;

    const now = new Date();
    const userData = {
      uid,
      type: Strings.TYPE,
      status: "active",
      created_at: now,
      updated_at: now,
      auth_version: 2,
      ...partial,
    };

    await this.apiService.setDocument(`users/${uid}`, userData, true);
    return userData;
  }

  private async upsertAuthFlags(uid: string, patch: any): Promise<void> {
    await this.apiService.setDocument(
      `users/${uid}`,
      {
        ...patch,
        updated_at: new Date(),
        auth_version: 2,
      },
      true
    );
  }

  // ---------------- Web Phone Auth ----------------
  private async initRecaptcha(containerElement: HTMLElement): Promise<void> {
    try {
      if (this.recaptchaVerifier) {
        try {
          this.recaptchaVerifier.clear();
        } catch {}
        this.recaptchaVerifier = null;
      }

      this.recaptchaVerifier = new RecaptchaVerifier(
        this.fireAuth,
        containerElement,
        {
          size: "normal",
          callback: () => {},
          "expired-callback": () => {
            try {
              this.recaptchaVerifier?.clear();
            } catch {}
            this.recaptchaVerifier = null;
          },
        }
      );

      await this.recaptchaVerifier.render();
    } catch (e) {
      throw this.friendlyAuthError(e, "فشل تهيئة التحقق. حاول مرة أخرى.");
    }
  }

  private async sendVerificationCodeWeb(
    phoneNumber: string,
    containerElement: HTMLElement
  ): Promise<string> {
    await this.initRecaptcha(containerElement);
    if (!this.recaptchaVerifier) throw new Error("تعذر تهيئة reCAPTCHA.");

    try {
      const provider = new PhoneAuthProvider(this.fireAuth);
      return await provider.verifyPhoneNumber(
        phoneNumber,
        this.recaptchaVerifier
      );
    } catch (e) {
      throw this.friendlyAuthError(e, "فشل إرسال رمز التحقق.");
    } finally {
      try {
        this.recaptchaVerifier?.clear();
      } catch {}
      this.recaptchaVerifier = null;
    }
  }

  // ---------------- Native Phone Auth ----------------
  private async sendVerificationCodeNative(
    phoneNumber: string
  ): Promise<string> {
    if (!this.isPluginAvailable("FirebaseAuthentication")) {
      throw new Error("FirebaseAuthentication plugin غير متاح على هذه المنصة.");
    }

    return new Promise<string>(async (resolve, reject) => {
      let sentHandle: any;
      let failHandle: any;

      const cleanup = async () => {
        try {
          await sentHandle?.remove?.();
        } catch {}
        try {
          await failHandle?.remove?.();
        } catch {}
      };

      try {
        sentHandle = await FirebaseAuthentication.addListener(
          "phoneCodeSent",
          (event: any) => {
            void cleanup();
            if (!event?.verificationId)
              return reject(new Error("لم يتم استلام verificationId."));
            resolve(event.verificationId);
          }
        );

        failHandle = await FirebaseAuthentication.addListener(
          "phoneVerificationFailed",
          (event: any) => {
            void cleanup();
            reject(new Error(event?.message || "فشل التحقق من رقم الهاتف."));
          }
        );

        await FirebaseAuthentication.signInWithPhoneNumber({ phoneNumber });
      } catch (e) {
        await cleanup();
        reject(this.friendlyAuthError(e, "فشل إرسال رمز التحقق."));
      }
    });
  }

  /**
   * ✅ Main entry (used by phone signup/login UI)
   * - Accepts +213... OR local formats (055..., 6.., 213..)
   * - Enforces ONE-SMS attempt per phone in a short window (cost control)
   */
  async sendVerificationCode(
    phoneNumber: string,
    containerElement?: HTMLElement
  ): Promise<string> {
    // ✅ PWA/Web: phone auth disabled
    if (!this.isNative()) {
      throw new Error(
        "تسجيل/دخول الهاتف غير متاح على نسخة الويب. استخدم البريد الإلكتروني وكلمة المرور."
      );
    }

    // normalize DZ
    const dz = this.normalizeDzPhoneAny(phoneNumber);
    const normalizedE164 = dz.e164;

    // cost control
    this.enforceSmsAttemptLimit(normalizedE164);

    if (this.isNative()) return this.sendVerificationCodeNative(normalizedE164);

    if (!containerElement)
      throw new Error(
        "في الويب: يجب توفير عنصر containerElement لـ reCAPTCHA."
      );

    return this.sendVerificationCodeWeb(normalizedE164, containerElement);
  }

  // ---------------- App Check helpers (Native) ----------------
  private async getNativeAppCheckTokenBestEffort(
    maxMs = 30000
  ): Promise<string | null> {
    if (!this.isPluginAvailable("FirebaseAppCheck")) return null;

    const start = Date.now();
    while (Date.now() - start < maxMs) {
      try {
        const r: any = await FirebaseAppCheck.getToken({ forceRefresh: false });
        if (r?.token) return r.token;
      } catch {}
      await new Promise((r) => setTimeout(r, 800));
    }
    return null;
  }

  // ---------------- Native -> Web session sync ----------------
  private async syncNativeAuthToWebIfNeeded(): Promise<void> {
    if (!this.isNative()) return;
    if (this.fireAuth.currentUser) return;

    if (!this.NATIVE_AUTH_CUSTOM_TOKEN_URL)
      throw new Error("nativeAuthCustomTokenUrl missing in environment.");
    if (!this.isPluginAvailable("FirebaseAuthentication"))
      throw new Error("FirebaseAuthentication plugin غير متاح.");

    const idTokenRes: any = await FirebaseAuthentication.getIdToken({
      forceRefresh: true,
    });
    const idToken = idTokenRes?.token;
    if (!idToken) throw new Error("Missing native ID token.");

    const appCheckToken = await this.getNativeAppCheckTokenBestEffort(30000);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);

    let raw = "";
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      };
      if (appCheckToken) headers["X-Firebase-AppCheck"] = appCheckToken;

      const resp = await fetch(this.NATIVE_AUTH_CUSTOM_TOKEN_URL, {
        method: "POST",
        redirect: "follow",
        signal: controller.signal,
        headers,
        body: JSON.stringify({ idToken }),
      });

      raw = await resp.text();
      if (!resp.ok)
        throw new Error(`Cloud Function failed (${resp.status}): ${raw}`);

      const json = JSON.parse(raw);
      const customToken = json?.customToken;
      if (!customToken) throw new Error(`Missing customToken: ${raw}`);

      await signInWithCustomToken(this.fireAuth, customToken);
      await this.waitForAuthReady(40, 250);
    } catch (e: any) {
      if (e?.name === "AbortError")
        throw new Error(
          "Cloud Function timeout. Check network / URL / region."
        );
      throw e;
    } finally {
      clearTimeout(timeout);
    }
  }

  // ---------------- Phone verify (OTP) ----------------
  async verifyCode(verificationId: string, code: string): Promise<any> {
    const otp = (code || "").trim();
    if (!otp) throw new Error("أدخل رمز التحقق.");

    try {
      if (this.isNative()) {
        if (!this.isPluginAvailable("FirebaseAuthentication"))
          throw new Error("FirebaseAuthentication plugin غير متاح.");

        const result: any =
          await FirebaseAuthentication.confirmVerificationCode({
            verificationId,
            verificationCode: otp,
          });

        const uid: string | undefined = result?.user?.uid;
        const phoneE164: string | null = result?.user?.phoneNumber || null;
        if (!uid) throw new Error("فشل تسجيل الدخول (لا يوجد uid).");

        await this.syncNativeAuthToWebIfNeeded();

        // ensure doc then mark phone verified (cheap + merge)
        await this.ensureUserDoc(uid, { name: "مستخدم" });

        if (phoneE164) {
          const dz = this.normalizeDzPhoneAny(phoneE164);
          await this.upsertAuthFlags(uid, {
            phone: dz.local10, // keep old field usage
            phone_e164: dz.e164,
            phone_locked: true,

            auth_phone_linked: true,
            auth_phone_verified: true,
            phone_verified_at: new Date(),

            // email flags will be set later in upgradeLinkEmailPassword
          });
        }

        this.setUserData(uid);
        return await this.getUserDataOrNull(uid);
      }

      const credential = PhoneAuthProvider.credential(verificationId, otp);
      const userCredential = await signInWithCredential(
        this.fireAuth,
        credential
      );

      const uid = userCredential?.user?.uid;
      if (!uid) return null;

      const phoneE164 = userCredential.user.phoneNumber || null;

      await this.ensureUserDoc(uid, { name: "مستخدم" });

      if (phoneE164) {
        const dz = this.normalizeDzPhoneAny(phoneE164);
        await this.upsertAuthFlags(uid, {
          phone: dz.local10,
          phone_e164: dz.e164,
          phone_locked: true,

          auth_phone_linked: true,
          auth_phone_verified: true,
          phone_verified_at: new Date(),
        });
      }

      this.setUserData(uid);
      return await this.getUserDataOrNull(uid);
    } catch (e: any) {
      console.error("[verifyCode] raw error:", e?.code, e?.message, e);
      throw this.friendlyAuthError(e, "فشل التحقق من الرمز.");
    }
  }

  // ---------------- Email/Password (login) ----------------
  // 2) In login(): ensure profile doc exists before type-check
  async login(email: string, password: string): Promise<any> {
    try {
      const response: any = await signInWithEmailAndPassword(
        this.fireAuth,
        email,
        password
      );
      if (!response?.user?.uid) return false;

      const uid = response.user.uid;

      // ✅ tolerate missing users/{uid}
      let profile = await this.getUserDataOrNull(uid);
      if (!profile) {
        profile = await this.ensureUserDoc(uid, {
          name: response.user.displayName || "مستخدم",
        });
      }

      if (profile?.type !== Strings.TYPE) {
        await this.logout();
        return false;
      }

      await this.upsertAuthFlags(uid, {
        email: String(email || "")
          .trim()
          .toLowerCase(),
        auth_email_linked: true,
        auth_has_password: true,
        auth_email_verified: !!response.user.emailVerified,
      });

      this.setUserData(uid);
      return profile?.type || Strings.TYPE;
    } catch (e) {
      throw this.friendlyAuthError(e, "فشل تسجيل الدخول.");
    }
  }

  // 3) In autoLoginGuard(): route phone-only sessions to upgrade-auth
  async autoLoginGuard() {
    try {
      const user = await this.getStartupUser();
      if (user?.uid) {
        this.setUserData(user.uid);

        const legacy = await this.isLegacyPhoneOnlySession(user);
        this.global.navigateByUrl(legacy ? "/upgrade-auth" : Strings.TABS);

        return false;
      }
      return true;
    } catch (e) {
      throw this.friendlyAuthError(e, "فشل التحقق من الجلسة.");
    }
  }

  // ---------------- Email/Password (signup or fallback) ----------------
  /**
   * ✅ Used for fallback email signup (when OTP fails)
   * - phone is optional
   * - phone saved in profile as UNVERIFIED if provided
   */
  async register(formValue: any) {
    try {
      const email = String(formValue?.email || "")
        .trim()
        .toLowerCase();
      const pass = String(formValue?.password || "");
      const name = String(formValue?.name || "").trim() || "مستخدم";
      const phoneRaw = String(formValue?.phone || "").trim();

      if (!email || !pass)
        throw new Error("يرجى إدخال البريد الإلكتروني وكلمة المرور.");

      const registeredUser = await createUserWithEmailAndPassword(
        this.fireAuth,
        email,
        pass
      );
      if (!registeredUser?.user) throw new Error("فشل إنشاء الحساب.");

      const uid = registeredUser.user.uid;
      const now = new Date();

      let phoneLocal: string | null = null;
      let phoneE164: string | null = null;

      if (phoneRaw) {
        const dz = this.normalizeDzPhoneAny(phoneRaw);
        phoneLocal = dz.local10;
        phoneE164 = dz.e164;
      }

      await this.apiService.setDocument(
        `users/${uid}`,
        {
          uid,
          type: Strings.TYPE,
          status: "active",
          created_at: now,
          updated_at: now,

          name,
          email,

          // keep old field usage
          phone: phoneLocal,
          phone_e164: phoneE164,

          // Manual phone (NOT verified)
          phone_locked: false,
          auth_phone_linked: false,
          auth_phone_verified: false,

          // Email/password is the main login
          auth_email_linked: true,
          auth_has_password: true,
          auth_email_verified: !!registeredUser.user.emailVerified,

          // markers
          auth_migrated_at: now,
          auth_version: 2,
          auth_signup_method: "email",
        },
        true
      );

      this.setUserData(uid);
      return { id: uid, type: Strings.TYPE };
    } catch (e) {
      throw this.friendlyAuthError(e, "فشل إنشاء الحساب.");
    }
  }

  async resetPassword(email: string) {
    try {
      await sendPasswordResetEmail(this.fireAuth, email);
    } catch (e) {
      throw this.friendlyAuthError(
        e,
        "فشل إرسال رابط إعادة تعيين كلمة المرور."
      );
    }
  }

  // ---------------- Session helpers ----------------
  async getId(): Promise<string | null> {
    const mem = this._uid.value?.uid;
    if (mem) return mem;

    const current = this.fireAuth.currentUser?.uid;
    if (current) {
      this.setUserData(current);
      return current;
    }

    try {
      const stored = await this.storage.getStorage(Strings.UID);
      const uid = stored?.value || null;
      if (uid) {
        this._uid.next(new AuthUserId(uid));
        return uid;
      }
    } catch {}

    return null;
  }

  setUserData(uid: string) {
    try {
      this.storage.setStorage(Strings.UID, uid);
    } catch {}
    this._uid.next(new AuthUserId(uid));
  }

  async getCurrentUser() {
    return this.fireAuth.currentUser;
  }

  checkAuth(): Promise<User | null> {
    return new Promise((resolve) =>
      onAuthStateChanged(this.fireAuth, (user) => resolve(user))
    );
  }

  /**
   * ✅ Get startup user reliably:
   * - If web session exists => return
   * - If native session exists => sync to web (best effort)
   * - Then wait for auth state once
   */
  async getStartupUser(): Promise<User | null> {
    try {
      if (this.fireAuth.currentUser) return this.fireAuth.currentUser;

      if (this.isNative()) {
        try {
          await this.syncNativeAuthToWebIfNeeded();
        } catch {}
        if (this.fireAuth.currentUser) return this.fireAuth.currentUser;
      }

      const user = await this.checkAuth();
      return user || null;
    } catch {
      return null;
    }
  }

  async introGuard() {
    const hasSeenIntro = await this.storage.getStorage(Strings.INTRO_KEY);
    if (hasSeenIntro?.value === "true") return true;
    this.global.navigateByUrl(Strings.WELCOME);
    return false;
  }

  // async autoLoginGuard() {
  //   try {
  //     const user = await this.getStartupUser();
  //     if (user?.uid) {
  //       this.setUserData(user.uid);
  //       this.global.navigateByUrl(Strings.TABS);
  //       return false;
  //     }
  //     return true;
  //   } catch (e) {
  //     throw this.friendlyAuthError(e, "فشل التحقق من الجلسة.");
  //   }
  // }

  async authGuard(): Promise<boolean> {
    try {
      const user = await this.getStartupUser();
      if (!user?.uid) {
        this.global.navigateByUrl(Strings.LOGIN);
        return false;
      }

      this.setUserData(user.uid);

      // read profile once
      let profile = await this.getUserDataOrNull(user.uid);

      // legacy safety: if no profile yet, create minimal
      if (!profile) {
        profile = await this.ensureUserDoc(user.uid, { name: "مستخدم" });
      }

      if (profile?.type !== Strings.TYPE) {
        await this.logout();
        return false;
      }

      return true;
    } catch (e) {
      console.error("Auth guard failed:", e);
      throw this.friendlyAuthError(e, "فشل التحقق من الصلاحيات.");
    }
  }

  // ---------------- Account maintenance ----------------
  async updateEmail(oldEmail: string, newEmail: string, password: string) {
    try {
      const result = await signInWithEmailAndPassword(
        this.fireAuth,
        oldEmail,
        password
      );
      await firebaseUpdateEmail(result.user, newEmail);
    } catch (e) {
      throw this.friendlyAuthError(e, "فشل تحديث البريد الإلكتروني.");
    }
  }

  async deleteUser(): Promise<void> {
    const user = this.fireAuth.currentUser;
    if (!user) throw new Error("المستخدم غير مسجل الدخول");

    try {
      const uid = user.uid;

      const addressSnap = await this.apiService.getDocs(`address/${uid}/all`);
      for (const d of addressSnap.docs) {
        await this.apiService.deleteDocument(`address/${uid}/all/${d.id}`);
      }

      await this.apiService.deleteDocument(`users/${uid}`);
      await firebaseDeleteUser(user);
    } catch (e: AnyError) {
      if (e?.code === "auth/requires-recent-login")
        throw new Error("يجب تسجيل الدخول مجددًا لحذف الحساب.");
      console.error("Delete user failed:", e);
      throw this.friendlyAuthError(e, "فشل حذف الحساب.");
    }
  }

  async waitForAuthReady(retries = 40, delayMs = 250): Promise<User> {
    for (let i = 0; i < retries; i++) {
      const user = this.fireAuth.currentUser;
      if (user) return user;
      await new Promise((res) => setTimeout(res, delayMs));
    }
    throw new Error("تعذر التحقق من تسجيل الدخول. الرجاء المحاولة مجددًا.");
  }

  async logout() {
    try {
      const prevUid =
        this.fireAuth.currentUser?.uid || this._uid.value?.uid || null;

      await this.fireAuth.signOut();

      if (this.isPluginAvailable("FirebaseAuthentication")) {
        try {
          await FirebaseAuthentication.signOut();
        } catch {}
      }

      try {
        await this.storage.removeStorage(Strings.UID);
      } catch {}
      this._uid.next(AuthService.UNKNOWN_USER);

      const cartService = this.injector.get(CartService);
      const orderService = this.injector.get(OrderService);
      const addressService = this.injector.get(AddressService);

      const push = this.injector.get(PushService);
      try {
        await push.disableForThisDevice(prevUid);
      } catch {}

      await cartService.clearCart();
      await cartService.clearCartOrder();
      cartService.reset();
      orderService.reset();
      addressService.reset();

      try {
        await this.storage.clearStorage();
      } catch {}
      try {
        localStorage.clear();
      } catch {}

      this.nativeOtpSubject.next(null);
      return true;
    } catch (e) {
      throw this.friendlyAuthError(e, "فشل تسجيل الخروج.");
    }
  }

  // ---------------- Data ----------------
  async getUserData(id: string) {
    const docSnap: any = await this.apiService.getDocById(`users/${id}`);
    if (docSnap?.exists?.()) return docSnap.data();
    throw new Error("لا توجد بيانات مستخدم.");
  }

  private async ensureJsAuthSession(): Promise<User> {
    if (this.fireAuth.currentUser) return this.fireAuth.currentUser;

    if (this.isNative()) {
      await this.syncNativeAuthToWebIfNeeded();
    }

    return await this.waitForAuthReady(40, 250);
  }

  /**
   * ✅ Upgrade existing (phone) account by linking Email/Password to SAME UID.
   * No email verification required.
   */
  async upgradeLinkEmailPassword(
    email: string,
    password: string,
    name?: string
  ): Promise<any> {
    try {
      const cleanEmail = (email || "").trim().toLowerCase();
      const cleanPass = (password || "").trim();

      if (!cleanEmail) throw new Error("أدخل البريد الإلكتروني.");
      if (!/^\S+@\S+\.\S+$/.test(cleanEmail))
        throw new Error("البريد الإلكتروني غير صالح.");
      if (cleanPass.length < 8)
        throw new Error("كلمة المرور يجب أن تكون 8 أحرف على الأقل.");

      const user = await this.ensureJsAuthSession();

      const hasPasswordProvider = (user.providerData || []).some(
        (p) => p?.providerId === "password"
      );
      const currentEmail = (user.email || "").trim().toLowerCase();

      if (hasPasswordProvider) {
        if (cleanEmail !== currentEmail) {
          const methods = await fetchSignInMethodsForEmail(
            this.fireAuth,
            cleanEmail
          );
          if (methods?.length > 0) {
            throw new Error(
              "هذا البريد مستخدم بالفعل. سجّل الدخول به أو اتصل بالدعم لدمج الحسابات."
            );
          }
          await firebaseUpdateEmail(user, cleanEmail);
        }
        await updatePassword(user, cleanPass);
      } else {
        const methods = await fetchSignInMethodsForEmail(
          this.fireAuth,
          cleanEmail
        );
        if (methods?.length > 0) {
          throw new Error(
            "هذا البريد مستخدم بالفعل. سجّل الدخول به أو اتصل بالدعم لدمج الحسابات."
          );
        }
        const cred = EmailAuthProvider.credential(cleanEmail, cleanPass);
        await linkWithCredential(user, cred);
      }

      const uid = user.uid;
      const existing = await this.getUserDataOrNull(uid);

      await this.apiService.setDocument(
        `users/${uid}`,
        {
          email: cleanEmail,
          name: (name || "").trim() || existing?.name || "مستخدم",

          // flags
          auth_email_linked: true,
          auth_has_password: true,
          auth_email_verified: !!user.emailVerified,

          auth_migrated_at: existing?.auth_migrated_at || new Date(),
          upgraded_at: new Date(),
          auth_signup_method: existing?.auth_signup_method || "phone",
        },
        true
      );

      this.setUserData(uid);
      return await this.getUserData(uid);
    } catch (e: any) {
      console.error("[upgradeLinkEmailPassword] raw:", e?.code, e?.message, e);

      if (e?.code === "auth/requires-recent-login") {
        throw new Error(
          "لأسباب أمنية، أعد تسجيل الدخول ثم حاول ترقية الحساب مرة أخرى."
        );
      }

      throw this.friendlyAuthError(e, "فشل ترقية الحساب. حاول مرة أخرى.");
    }
  }

  /**
   * ✅ Save phone number in Firestore profile (manual, unverified)
   * Use for fallback signup flows.
   */
  async setPhoneOnce(phoneNumber: string): Promise<void> {
    const dz = this.normalizeDzPhoneAny(phoneNumber);
    const uid = await this.getId();
    if (!uid) throw new Error("يجب تسجيل الدخول أولاً.");

    const existing = await this.getUserDataOrNull(uid);
    if (existing?.phone && existing?.phone_locked) return;

    await this.apiService.setDocument(
      `users/${uid}`,
      {
        phone: existing?.phone || dz.local10,
        phone_e164: existing?.phone_e164 || dz.e164,
        phone_locked: false,
        auth_phone_linked: existing?.auth_phone_linked ?? false,
        auth_phone_verified: existing?.auth_phone_verified ?? false,
        updated_at: new Date(),
      },
      true
    );
  }

  /**
   * ✅ Block access to /app if user still phone-only (no password provider).
   * Redirect to /upgrade-auth.
   */
  async upgradeGuard(): Promise<boolean> {
    try {
      const user = await this.getStartupUser();

      if (!user?.uid) {
        this.global.navigateByUrl(Strings.LOGIN);
        return false;
      }

      const hasPasswordProvider = (user.providerData || []).some(
        (p) => p?.providerId === "password"
      );
      if (hasPasswordProvider) return true;

      const profile = await this.getUserDataOrNull(user.uid);
      if (profile?.auth_has_password === true) return true;

      this.global.navigateByUrl("/upgrade-auth");
      return false;
    } catch (e) {
      console.error("[upgradeGuard] failed:", e);
      this.global.navigateByUrl("/upgrade-auth");
      return false;
    }
  }

  /**
   * Legacy migration detector:
   * user is signed-in AND the session came from phone provider (old) AND NOT yet upgraded to email/password.
   */
  async isLegacyPhoneOnlySession(user?: User | null): Promise<boolean> {
    try {
      const u = user ?? (await this.getStartupUser());
      if (!u?.uid) return false;

      const providers = (u.providerData || [])
        .map((p) => p?.providerId)
        .filter(Boolean);
      const hasPassword = providers.includes("password");
      const hasPhone = providers.includes("phone");

      if (hasPassword) return false;
      if (!hasPhone) return false;

      const profile = await this.getUserDataOrNull(u.uid);
      if (profile?.auth_has_password === true) return false;

      return true;
    } catch {
      return false;
    }
  }
}
